// SDK Documentation Page
import { ArrowLeft, Check, Copy } from 'lucide-react'
import { useState } from 'react'
import Button from '../components/ui/Button'
import Card from '../components/ui/Card'
import {
  ANDROID_SDK_COORDINATE,
  ANDROID_SDK_REPO_URL,
  ANDROID_SDK_REQUIREMENTS,
  ANDROID_SDK_VERSION,
  APPLE_SDK_REPO_URL,
  APPLE_SDK_REQUIREMENTS,
  APPLE_SDK_VERSION,
} from '../utils/sdkVersions'

interface SDKDocsProps {
  onBack: () => void
}

type PlatformTab = 'apple' | 'android'

const appleInstall = `// Package.swift
dependencies: [
    .package(url: "${APPLE_SDK_REPO_URL}.git", from: "${APPLE_SDK_VERSION}")
]`

const appleConfigure = `import AppStats
import SwiftUI

@main
struct YourApp: App {
    init() {
        // Call this as the very first line of app startup.
        AppStats.configure(apiKey: "YOUR_API_KEY")
    }

    var body: some Scene {
        WindowGroup {
            ContentView()
        }
    }
}`

const appleEvents = `import AppStats

// Custom events — property values must be primitives
AppStats.track("purchase_completed", properties: [
    "product_id": "premium_subscription",
    "price": 9.99,
    "currency": "USD"
])

// Sticky properties, merged into every subsequent event
AppStats.setUserProperty("subscription_tier", value: "pro")

// Screen views are tracked automatically for UIKit and SwiftUI.
// Name a SwiftUI screen explicitly with the view modifier:
NavigationStack {
    HomeView()
        .trackedScreen("Home")
}

// Crashes are captured automatically and replayed on the next launch.`

const androidInstall = `// settings.gradle.kts
dependencyResolutionManagement {
    repositoriesMode.set(RepositoriesMode.FAIL_ON_PROJECT_REPOS)
    repositories {
        google()
        mavenCentral()
        maven("https://jitpack.io")
    }
}

// app/build.gradle.kts
dependencies {
    implementation("${ANDROID_SDK_COORDINATE}")
}`

const androidConfigure = `// Application class
import android.app.Application
import com.onethumsoftware.appstats.AppStats

class YourApplication : Application() {
    override fun onCreate() {
        super.onCreate()
        AppStats.configure(this, apiKey = "YOUR_API_KEY")
    }
}

// Register it in AndroidManifest.xml:
// <application android:name=".YourApplication" ...>

// Zero-code alternative — declare the key as manifest meta-data and the
// bundled androidx.startup initializer configures the SDK for you:
// <meta-data
//     android:name="com.onethumsoftware.appstats.API_KEY"
//     android:value="YOUR_API_KEY" />`

const androidEvents = `import com.onethumsoftware.appstats.AppStats

// Custom events — property values must be primitives
AppStats.track(
    "purchase_completed",
    mapOf(
        "product_id" to "premium_subscription",
        "price" to 9.99,
        "currency" to "USD",
    ),
)

// Sticky properties, merged into every subsequent event
AppStats.setUserProperty("subscription_tier", "pro")

// Identify a signed-in user (sets sticky user_id + signed_in)
AppStats.identify(userId)

// Activities are tracked automatically. Call trackScreen from Compose
// navigation or any custom router where the Activity name is not useful:
AppStats.trackScreen("Settings")

// Crashes (uncaught exceptions) are captured automatically and
// delivered on the next launch.`

const telemetryFields = [
  ['Platform', 'ios, macos, visionos, tvos, watchos, android'],
  ['SDK producer', 'swift or kotlin from X-AS-SDK-Platform'],
  ['SDK version', 'sdk_version for adoption and rollout analysis'],
  ['App version/build', 'app_version and build_number'],
  ['Device/OS', 'device_model, os_version, screen_resolution'],
  ['Locale/timezone', 'locale and timezone'],
  ['Session/event', 'session_id, event_type, event_name, screen_name'],
  ['Location', 'country/city/coordinates enriched server-side from IP'],
]

export default function SDKDocs({ onBack }: SDKDocsProps) {
  const [copiedSection, setCopiedSection] = useState<string | null>(null)
  const [platform, setPlatform] = useState<PlatformTab>('apple')

  const handleCopy = (text: string, section: string) => {
    navigator.clipboard.writeText(text)
    setCopiedSection(section)
    window.setTimeout(() => setCopiedSection(null), 2000)
  }

  const isApple = platform === 'apple'
  const installCode = isApple ? appleInstall : androidInstall
  const configureCode = isApple ? appleConfigure : androidConfigure
  const eventsCode = isApple ? appleEvents : androidEvents

  return (
    <div className="flex h-full flex-col">
      <div className="border-b border-white/10 p-4 md:p-6">
        <button
          onClick={onBack}
          className="mb-4 flex items-center gap-2 text-gray-400 transition-colors hover:text-white"
        >
          <ArrowLeft size={20} />
          <span>Back</span>
        </button>
        <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-[0.22em] text-indigo-300">AppStats SDKs</p>
            <h1 className="text-3xl font-bold tracking-tight md:text-4xl">SDK Documentation</h1>
            <p className="mt-2 max-w-3xl text-gray-400">
              Integrate AppStats with Apple platforms via Swift Package Manager or Android via Kotlin/Gradle.
              Both SDKs send the same protocol so the dashboard can compare Android, iOS, and other platforms directly.
            </p>
          </div>
          <div className="flex rounded-2xl border border-white/10 bg-white/[0.035] p-1">
            <PlatformButton active={platform === 'apple'} onClick={() => setPlatform('apple')}>Apple / Swift</PlatformButton>
            <PlatformButton active={platform === 'android'} onClick={() => setPlatform('android')}>Android / Kotlin</PlatformButton>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-auto p-4 md:p-6 custom-scrollbar">
        <div className="mx-auto max-w-5xl space-y-6">
          <Card>
            <h2 className="mb-4 text-2xl font-semibold">Quick Start</h2>
            <div className="grid gap-4 md:grid-cols-4">
              {[
                ['1', 'Create an app', 'Register an iOS, macOS, visionOS, tvOS, watchOS, or Android app in Apps.'],
                ['2', 'Create an API key', 'Use Settings to create or copy the app API key.'],
                ['3', 'Install the SDK', isApple ? 'Add the Swift package in Xcode or Package.swift.' : 'Add JitPack and the Android SDK dependency.'],
                ['4', 'Send telemetry', 'Launch the app, track screens/events, then inspect Live and Breakdowns.'],
              ].map(([step, title, description]) => (
                <div key={step} className="rounded-2xl border border-white/10 bg-white/[0.035] p-4">
                  <div className="mb-3 flex h-8 w-8 items-center justify-center rounded-full bg-indigo-500/20 font-semibold text-indigo-200">{step}</div>
                  <h3 className="font-semibold text-white">{title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-gray-400">{description}</p>
                </div>
              ))}
            </div>
          </Card>

          <CodeCard
            title={isApple ? '1. Install with Swift Package Manager' : '1. Install with Gradle/JitPack'}
            description={isApple
              ? `Use the public OneThum/AppStats-iOS package, version ${APPLE_SDK_VERSION} or later. Requires ${APPLE_SDK_REQUIREMENTS}.`
              : `Use the public OneThum/AppStats-Android package, version ${ANDROID_SDK_VERSION} or later. Requires ${ANDROID_SDK_REQUIREMENTS}. Releases are tagged without a "v" prefix, so the JitPack version matches the tag exactly.`}
            code={installCode}
            section={`${platform}-install`}
            copiedSection={copiedSection}
            onCopy={handleCopy}
          />

          <CodeCard
            title="2. Configure at launch"
            description={isApple
              ? 'Configure AppStats once during app startup, before tracking screens or events.'
              : 'Configure AppStats from your Application class so lifecycle and session capture starts immediately — or skip the code entirely with manifest meta-data.'}
            code={configureCode}
            section={`${platform}-configure`}
            copiedSection={copiedSection}
            onCopy={handleCopy}
          />

          <CodeCard
            title="3. Track screens and events"
            description="Use consistent, descriptive event names. Properties must be primitive JSON values."
            code={eventsCode}
            section={`${platform}-events`}
            copiedSection={copiedSection}
            onCopy={handleCopy}
          />

          <Card>
            <h2 className="mb-4 text-2xl font-semibold">What the Dashboard Shows</h2>
            <p className="mb-4 text-gray-400">
              The dashboard now surfaces Android vs iOS/platform mix, SDK producer, SDK version, app build,
              device metadata, screen resolution, locale, and timezone in Live, Breakdowns, and Event Explorer.
            </p>
            <div className="grid gap-3 md:grid-cols-2">
              {telemetryFields.map(([label, value]) => (
                <div key={label} className="rounded-2xl border border-white/10 bg-gray-800/40 p-4">
                  <div className="text-sm font-semibold text-indigo-300">{label}</div>
                  <div className="mt-1 text-sm text-gray-400">{value}</div>
                </div>
              ))}
            </div>
          </Card>

          <Card>
            <h2 className="mb-4 text-2xl font-semibold">Testing Your Integration</h2>
            <div className="space-y-3 text-sm text-gray-300">
              <p>1. Run the app on {isApple ? 'a real device or the iOS Simulator' : 'a real device or an Android emulator'}.</p>
              <p>
                2. Confirm startup in {isApple ? 'the Xcode console' : 'Logcat'} — look for the{' '}
                <code className="rounded bg-black/40 px-1.5 py-0.5 text-xs text-indigo-200">[AppStats]</code>{' '}
                {isApple ? 'initialization line.' : 'tag. Enable verbose SDK logging with debugLogging = true in AppStatsConfiguration.'}
              </p>
              <p>3. Trigger an app launch, a screen view, and a custom event.</p>
              <p>
                4. Open the Live tab to confirm events stream in realtime. The default flush interval is 30 seconds — call{' '}
                <code className="rounded bg-black/40 px-1.5 py-0.5 text-xs text-indigo-200">AppStats.flush()</code>{' '}
                to send immediately.
              </p>
              <p>5. Open Breakdowns to confirm Platform, SDK Producer, SDK Version, App Version, Build Number, Device, OS, Locale, and Timezone populate.</p>
              <p>6. Use Event Explorer to inspect raw event properties and metadata.</p>
              {!isApple && (
                <p>
                  7. Building with R8/ProGuard? The SDK ships its own consumer rules, so no extra
                  configuration is needed — but keep the mapping file for the release you shipped so
                  crash stack traces stay readable.
                </p>
              )}
            </div>
            <p className="mt-4 text-sm text-gray-500">
              Source: <a className="text-indigo-300 hover:text-indigo-200" href={isApple ? APPLE_SDK_REPO_URL : ANDROID_SDK_REPO_URL} target="_blank" rel="noreferrer">{isApple ? 'OneThum/AppStats-iOS' : 'OneThum/AppStats-Android'}</a>
            </p>
          </Card>

          <Card>
            <h2 className="mb-4 text-2xl font-semibold">API Key Security</h2>
            <div className="space-y-3 text-sm text-gray-400">
              <p><strong className="text-white">Do not commit API keys.</strong> Store keys in configuration excluded from version control.</p>
              <p><strong className="text-white">Use separate keys per app/environment.</strong> Keep development, staging, and production telemetry isolated.</p>
              <p><strong className="text-white">Rotate keys from Settings.</strong> Create a replacement key before deleting an old key from a shipped build.</p>
            </div>
          </Card>
        </div>
      </div>
    </div>
  )
}

function PlatformButton({ active, onClick, children }: { active: boolean; onClick: () => void; children: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`mobile-tap-target rounded-xl px-4 text-sm font-semibold transition-colors ${
        active ? 'bg-white text-gray-950' : 'text-gray-400 hover:bg-white/10 hover:text-white'
      }`}
    >
      {children}
    </button>
  )
}

function CodeCard({
  title,
  description,
  code,
  section,
  copiedSection,
  onCopy,
}: {
  title: string
  description: string
  code: string
  section: string
  copiedSection: string | null
  onCopy: (text: string, section: string) => void
}) {
  const copied = copiedSection === section
  return (
    <Card>
      <div className="mb-4 flex items-start justify-between gap-4">
        <div>
          <h2 className="text-2xl font-semibold">{title}</h2>
          <p className="mt-2 text-sm leading-relaxed text-gray-400">{description}</p>
        </div>
        <Button variant="secondary" size="sm" onClick={() => onCopy(code, section)}>
          {copied ? <Check size={16} /> : <Copy size={16} />}
          <span className="ml-2">{copied ? 'Copied' : 'Copy'}</span>
        </Button>
      </div>
      <div className="overflow-x-auto rounded-2xl border border-white/10 bg-gray-950 p-4">
        <pre className="whitespace-pre text-xs leading-relaxed text-gray-300 md:text-sm">
          {code}
        </pre>
      </div>
    </Card>
  )
}
